/**
 * Groups adjacent implied (auto) project time entries for cleaner UI.
 * Firestore rows stay as-is; this is display-only.
 */

import type { ProjectTimeEntry } from '../types';

/** Merge implied segments when gap between end → next start is at most this (ms). */
export const PROJECT_TIME_MERGE_GAP_MS = 5 * 60 * 1000;

export type ProjectTimeDisplayGroup =
  | { kind: 'single'; entry: ProjectTimeEntry }
  | { kind: 'merged'; entries: ProjectTimeEntry[] };

function entryStartTime(e: ProjectTimeEntry): Date {
  return e.startTime ?? e.date;
}

function entryEndTime(e: ProjectTimeEntry): Date {
  return e.endTime ?? e.date;
}

function isImpliedAuto(e: ProjectTimeEntry): boolean {
  return e.type === 'implied' && !e.note?.trim();
}

/**
 * Build display groups: consecutive implied entries (no note) with small gaps are merged.
 * Explicit entries and implied-with-note stay as single rows.
 */
export function buildProjectTimeDisplayGroups(entries: ProjectTimeEntry[]): ProjectTimeDisplayGroup[] {
  const implied = entries.filter(isImpliedAuto);
  const other = entries.filter(e => !isImpliedAuto(e));

  const sorted = [...implied].sort((a, b) => entryStartTime(a).getTime() - entryStartTime(b).getTime());
  const chunks: ProjectTimeEntry[][] = [];

  for (const e of sorted) {
    const prev = chunks[chunks.length - 1];
    if (prev) {
      const last = prev[prev.length - 1];
      const gap = entryStartTime(e).getTime() - entryEndTime(last).getTime();
      if (gap <= PROJECT_TIME_MERGE_GAP_MS) {
        prev.push(e);
        continue;
      }
    }
    chunks.push([e]);
  }

  const groups: ProjectTimeDisplayGroup[] = [];
  for (const arr of chunks) {
    groups.push(arr.length === 1 ? { kind: 'single', entry: arr[0] } : { kind: 'merged', entries: arr });
  }
  for (const e of other) {
    groups.push({ kind: 'single', entry: e });
  }

  groups.sort((a, b) => {
    const endA = a.kind === 'single' ? entryEndTime(a.entry) : entryEndTime(a.entries[a.entries.length - 1]);
    const endB = b.kind === 'single' ? entryEndTime(b.entry) : entryEndTime(b.entries[b.entries.length - 1]);
    return endB.getTime() - endA.getTime();
  });

  return groups;
}

export function sumMinutesInGroup(group: ProjectTimeDisplayGroup): number {
  if (group.kind === 'single') return group.entry.minutes;
  return group.entries.reduce((s, e) => s + e.minutes, 0);
}
